import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import moment from 'moment'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import AddIcon from '@mui/icons-material/Add'
import ErrorIcon from '@mui/icons-material/Error'
import SearchIcon from '@mui/icons-material/Search'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import QRCodeReader from '../../../helpers/QRCodeReader'
import useIsMobile from '../../../helpers/useIsMobile'
import AssetMakeReadyBlock from '../makeReadyModule/AssetMakeReadyBlock'
import {
  Clear,
  ContentCopy,
  OpenInNew,
  QrCode,
  WarningAmber,
} from '@mui/icons-material'

const AssetManager = () => {
  const [loading, setLoading] = useState(true)
  const [assets, setAssets] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [openQRReader, setOpenQRReader] = useState(false)
  const isMobile = useIsMobile()

  const fetchAssets = () => {
    setLoading(true)
    API.get('/assets', { params: { q: search } })
      .then((res) => {
        if (res.status === 200) {
          setAssets(() => res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!search) {
      fetchAssets({ search: '' })
    }

    if (search.length < 2) return

    const timeout = setTimeout(() => {
      fetchAssets()
    }, 300)

    return () => clearTimeout(timeout)
  }, [search])

  const handleQRSearch = (QrCode) => {
    const extractId = (value) => {
      try {
        // If it's a full URL, parse it
        const url = new URL(value)
        const parts = url.pathname.split('/').filter(Boolean)
        return parts[parts.length - 1]
      } catch {
        // If it's not a URL, assume it's already an ID
        return value
      }
    }
    const id = extractId(QrCode)
    setLoading(true)
    API.get(`/asset/lookupID/${id}`)
      .then((res) => {
        if (res.status === 200) {
          navigate(`/asset-manager/${id}`)
          onClose()
        } else if (res.status === 404) {
          setAssets([])
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  const columns = [
    { field: 'assetID', headerName: 'Asset ID', minWidth: isMobile ? 130 : 200 },
    {
      field: 'currentUnavailability',
      headerName: 'Availability',
      minWidth: 140,
      renderCell: ({ value }) =>
        value ? (
          <Chip
            size="small"
            color="warning"
            label={value.type || 'Unavailable'}
            title={value.reason || undefined}
            sx={{ borderRadius: 1 }}
          />
        ) : null,
    },
    { field: 'category', headerName: 'Category', flex: 1, minWidth: 150 },
    {
      field: 'subCategory',
      headerName: 'Sub Category',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'makeReadyLoadList',
      headerName: 'Make Ready',
      flex: 1,
      valueGetter: (value) => value?.title,
    },
    {
      field: 'manufacturer',
      headerName: 'Manufacturer',
      minWidth: 200,
      flex: 1,
    },
    { field: 'model', headerName: 'Model', minWidth: 140, flex: 1 },
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'vehicle',
      headerName: 'Location',
      minWidth: 120,
      flex: 1,
      valueGetter: (value, row) =>
        value?.callSign ??
        (row?.parentAsset?.assetID
          ? `In ${row.parentAsset.assetID}${
              row.parentAsset.vehicle?.callSign
                ? ` (on ${row.parentAsset.vehicle.callSign})`
                : ''
            }`
          : row?.store?.name
            ? `In ${row.store.name}${
                row.store.baseLocation?.name
                  ? ` (${row.store.baseLocation.name})`
                  : ''
              }`
            : undefined),
    },
    {
      field: 'event',
      headerName: 'Event',
      minWidth: 120,
      flex: 1,
      valueGetter: (value) => value?.title,
    },
    {
      field: 'checkedOutTo',
      headerName: 'Checked Out',
      minWidth: 200,
      flex: 1,
      valueGetter: (value) =>
        value ? `${value.firstName + ' ' + value.lastName}` : null,
    },
    {
      field: 'lastServiced',
      headerName: 'Last Service',
      type: 'date',
      minWidth: 120,
      valueGetter: (value, row) =>
        row?.latestMaintenanceLog?.maintenanceDate
          ? new Date(row.latestMaintenanceLog.maintenanceDate)
          : null,
      valueFormatter: (value) =>
        value ? moment(value).format('DD/MM/YYYY') : '',
    },
    {
      field: 'serviceDue',
      headerName: 'Service',
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => {
        if (!row.requiresServicing) {
          return null
        } else if (
          moment(row?.latestMaintenanceLog?.expiry).isBefore(
            moment().startOf('day'),
          ) ||
          !row.latestMaintenanceLog
        ) {
          return <CancelIcon color="error" />
        } else if (
          moment(row.latestMaintenanceLog?.expiry).isBefore(
            moment().add(30, 'days'),
          )
        ) {
          return <ErrorIcon color="warning" />
        } else {
          return <CheckCircleIcon color="success" />
        }
      },
    },
    {
      field: 'defects',
      headerName: 'Defects',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.row.currentDefects?.length > 0 ? (
          <ErrorIcon color="error" />
        ) : null,
    },
  ]

  return (
    <Stack gap={1}>
      {loading && <LinearProgress />}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={{ xs: 1.5, md: 2 }}
        justifyContent="space-between"
      >
        <Typography variant="title2">Asset Manager</Typography>
        {/* Phones: search on its own full-width row, then Add New + QR Scan
            side by side. lg+: the original single row. */}
        <Stack direction="row" flexWrap="wrap" gap={{ xs: 1, lg: 2 }}>
          <Button
            onClick={() => navigate('add-new')}
            variant="contained"
            color="info"
            startIcon={<AddIcon />}
            sx={{ order: { xs: 2, lg: 1 }, flex: { xs: 1, lg: 'none' } }}
          >
            Add New
          </Button>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Asset ID, category, make ready list…"
            size="small"
            sx={{
              width: { xs: '100%', lg: 360 },
              flexBasis: { xs: '100%', lg: 'auto' },
              order: { xs: 1, lg: 2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            autoComplete="none"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<QrCode />}
            onClick={() => setOpenQRReader(true)}
            sx={{ order: 3, flex: { xs: 1, lg: 'none' } }}
          >
            QR Scan
          </Button>

          <QRCodeReader
            open={openQRReader}
            onClose={() => setOpenQRReader(false)}
            onScan={(code) => handleQRSearch(code)}
          />
        </Stack>
      </Stack>
      {fetchError ? (
        <Alert severity="error">
          Unable to fetch assets. Please try again later.
        </Alert>
      ) : (
        <CustomDataGrid
          maxHeight={500}
          rows={assets}
          columns={columns}
          getRowId={(row) => row._id}
          onRowClick={(params) => setSelectedAsset(params.row)}
          sx={{
            '.MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                manufacturer: false,
                model: false,
                serialNumber: false,
                checkedOutTo: false,
                event: false,
                vehicle: false,
              },
            },
          }}
        />
      )}
      <Drawer
        open={!!selectedAsset}
        onClose={() => setSelectedAsset(null)}
        anchor="right"
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            bgcolor: '#fff',
            borderLeft: `1px solid`,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Asset</Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Copy Asset ID">
                <IconButton
                  onClick={() =>
                    navigator.clipboard.writeText(selectedAsset?.assetID)
                  }
                  disabled={!selectedAsset}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setSelectedAsset(null)}>
                <Clear />
              </IconButton>
            </Stack>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              >
                {selectedAsset?.assetID}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedAsset?.category}{' '}
                {selectedAsset?.subCategory
                  ? `— ${selectedAsset.subCategory}`
                  : ''}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {/* <Chip size="small" label={selectedAsset?.status} /> */}
              {selectedAsset?.currentDefects?.length > 0 ? (
                <Chip
                  size="small"
                  label="Defect(s)"
                  icon={<WarningAmber sx={{ color: '#fff !important' }} />}
                  color="anRed"
                  sx={{ borderRadius: 1 }}
                />
              ) : null}
            </Stack>
            <Grid container rowSpacing={1}>
              {selectedAsset?.manufacturer && (
                <Grid item xs={12} lg={6}>
                  <Stack>
                    <Typography variant="caption">Manufacturer</Typography>
                    <Typography>{selectedAsset.manufacturer}</Typography>
                  </Stack>
                </Grid>
              )}
              {selectedAsset?.brand && (
                <Grid item xs={12} lg={6}>
                  <Stack>
                    <Typography variant="caption">Brand</Typography>
                    <Typography>{selectedAsset.brand}</Typography>
                  </Stack>
                </Grid>
              )}
              {selectedAsset?.model && (
                <Grid item xs={12} lg={6}>
                  <Stack>
                    <Typography variant="caption">Model</Typography>
                    <Typography variant="body1">
                      {selectedAsset.model}
                    </Typography>
                  </Stack>
                </Grid>
              )}
            </Grid>
            <AssetMakeReadyBlock
              assetId={selectedAsset?._id}
              onChanged={(loadList) => {
                // Keep the grid's Make Ready column in step without a refetch.
                setAssets((prev) =>
                  prev.map((a) =>
                    a._id === selectedAsset?._id
                      ? { ...a, makeReadyLoadList: loadList }
                      : a,
                  ),
                )
                setSelectedAsset((prev) =>
                  prev ? { ...prev, makeReadyLoadList: loadList } : prev,
                )
              }}
            />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={1}>
            <Button
              variant="contained"
              startIcon={<OpenInNew />}
              onClick={() => navigate(selectedAsset?.assetID)}
              fullWidth
            >
              Open full record
            </Button>
            <Button
              variant="outlined"
              startIcon={<WarningAmber />}
              onClick={() => {
                navigate(
                  `/report-defect?assetIDLabel=${selectedAsset?.assetID}`,
                )
              }}
            >
              Log defect
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Stack>
  )
}

export default AssetManager
